import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ArrowLeft,
  BarChart3,
  Code2,
  Eraser,
  Filter,
  Globe,
  Globe2,
  Pencil,
  Plus,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
} from 'lucide-react';
import type { AnalyticsApi } from '../../api/analytics';
import { widgetInstallId } from '../../api/siteIntegrations';
import type { AnalyticsSite } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { RailItem, type RailEntry } from '../../components/setup/SetupKit';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { useLoad } from '../../lib/useLoad';
import { useTimeFormat } from '../../lib/tenantTime';
import { INSTALL_NAME_MAX, type InstallSection, type InstallState, installState } from './installForm';
import {
  Choice,
  CollectionSection,
  DataSection,
  FunnelSection,
  LocationSection,
  PrivacySection,
  SetupSection,
} from './InstallSections';

/**
 * An install's settings: one place to get the tag onto a site, and one place
 * per question after that.
 *
 * It used to be seven cards in a single 780px column — the snippet, every
 * collection setting, the funnel, privacy, erasure, removal and the CRM-wide
 * location key, about 3,800px of scroll — so the operator who came to exclude
 * their office IP read past the snippet to find it, and a change made on one
 * card and not saved was lost to a Save button three cards down. The rail names
 * the questions and shows one answer at a time, the way the Communications
 * settings do; the first answer is the setup itself, laid out as the steps it
 * is done in, beside whether it worked.
 *
 * It is a panel inside AnalyticsView rather than a route of its own because the
 * same component has to work inside the dashboard's iframe, which has no router
 * to navigate. For the same reason the section is NOT in the URL — `?tab=` is
 * the embed protocol (console/README.md), and a place inside one screen is not
 * a destination anything links to.
 */

interface SectionDef extends RailEntry<InstallSection> {
  /** Shown only at `manage` — a reader could not change anything there. */
  manageOnly: boolean;
}

const INSTALL_SECTIONS: SectionDef[] = [
  { key: 'install', label: 'Install', hint: 'The code, and whether data arrives', icon: Code2, manageOnly: false },
  {
    key: 'collection',
    label: 'Collection',
    hint: 'What counts, and what is left out',
    icon: SlidersHorizontal,
    manageOnly: true,
  },
  { key: 'funnel', label: 'Reservation funnel', hint: 'The steps to a reservation, and leads', icon: Filter, manageOnly: true },
  { key: 'privacy', label: 'Privacy', hint: 'Cookies, Do Not Track, retention', icon: ShieldCheck, manageOnly: true },
  { key: 'data', label: 'Data & removal', hint: 'Erase a visitor, remove the install', icon: Eraser, manageOnly: true },
];

/** An app install has no code to copy: its Install section is one switch. */
const APP_INSTALL_HINT = 'On or off, and whether data arrives';

/** Under its own heading because it is not this install's: one key serves every install. */
const SHARED_SECTIONS: SectionDef[] = [
  { key: 'location', label: 'Location data', hint: 'Town and region, for every install', icon: Globe2, manageOnly: false },
];

/** The dashboard header's chip, plus the one state that screen does not need to say. */
const CHIP: Record<InstallState, { tone: 'ok' | 'warn'; label: string }> = {
  ok: { tone: 'ok', label: 'Reporting' },
  waiting: { tone: 'warn', label: 'No data yet' },
  quiet: { tone: 'warn', label: 'Quiet 24h' },
  off: { tone: 'warn', label: 'Collecting off' },
};

export function InstallManage({
  api,
  site,
  sites,
  mayManage,
  initialSection,
  refreshing = false,
  onChanged,
  onSelect,
  onCreated,
  onClose = null,
  closeLabel,
  onRemoved,
}: {
  api: AnalyticsApi;
  site: AnalyticsSite;
  sites: AnalyticsSite[];
  mayManage: boolean;
  /** Where to open. Without one — or one this reader cannot see — Install. */
  initialSection?: InstallSection;
  /** The install list is being re-read, which is what "Check again" waits on. */
  refreshing?: boolean;
  onChanged: () => void;
  onSelect: (id: number) => void;
  onCreated: (id: number) => void;
  /**
   * The way out, above the heading — guarded like every other way of leaving
   * an install. Inside the analytics screen it is back to its reports; on the
   * settings-only page it is the reports wherever the host keeps them, and
   * absent when there is nowhere to send the reader.
   */
  onClose?: (() => void) | null;
  /** What the way out says. Defaults to the analytics screen's own. */
  closeLabel?: ReactNode;
  /**
   * The install was removed. The list has already been re-read and the next
   * install selected, so a page that is only settings has nothing to do; the
   * analytics screen goes back to its reports.
   */
  onRemoved?: () => void;
}) {
  const { fmtDate } = useTimeFormat();
  const all = [...INSTALL_SECTIONS, ...SHARED_SECTIONS];
  const allowed = (key: InstallSection | undefined) =>
    all.some((entry) => entry.key === key && (mayManage || !entry.manageOnly));
  const [section, setSection] = useState<InstallSection>(() =>
    initialSection && allowed(initialSection) ? initialSection : 'install',
  );
  const [dirty, setDirty] = useState<Partial<Record<InstallSection, boolean>>>({});
  // A rename half typed on the title is an edit like any section's.
  const [renaming, setRenaming] = useState(false);
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  // Lifted out of the Location section because the setup checklist asks the
  // same question ("can visits resolve to a town?").
  const geo = useLoad(() => api.getAnalyticsGeo(), []);
  // Whether the booking-widget pages report to THIS install — the dispatch
  // dashboard's Widgets switch. A failed read, or a service that has no such
  // switch yet, is "no": the Install section then reads as it always has.
  const integrations = useLoad(() => api.getSiteIntegrations(), []);
  const widgetLinked = widgetInstallId(integrations.data) === site.id;

  const onDirty = useCallback(
    (key: InstallSection, value: boolean) =>
      setDirty((was) => (Boolean(was[key]) === value ? was : { ...was, [key]: value })),
    [],
  );
  const unsaved = [
    ...(renaming ? ['The name'] : []),
    ...all.filter((entry) => dirty[entry.key]).map((entry) => entry.label),
  ];

  /** Run something that leaves this install, unless it would throw edits away. */
  const guard = (action: () => void) => {
    if (unsaved.length) setPending(() => action);
    else action();
  };

  const go = (key: InstallSection) => {
    setSection(key);
    // The rail is pinned and a section can be long: chosen from the foot of
    // another, the new one would otherwise open halfway down.
    const pane = paneRef.current;
    if (pane && pane.getBoundingClientRect().top < 0) pane.scrollIntoView({ block: 'start', inline: 'nearest' });
  };

  const state = installState(site);
  // An app install has no Collecting switch to name — its Install switch is it.
  const chip = state === 'off' && site.platform === 'app' ? { ...CHIP.off, label: 'Switched off' } : CHIP[state];
  const visible = (entries: SectionDef[]) => entries.filter((entry) => mayManage || !entry.manageOnly);

  const navItem = (entry: SectionDef) => (
    <RailItem
      key={entry.key}
      entry={entry.key === 'install' && site.platform === 'app' ? { ...entry, hint: APP_INSTALL_HINT } : entry}
      on={section === entry.key}
      dirty={Boolean(dirty[entry.key])}
      status={entry.key === 'install' ? chip : null}
      onGo={go}
    />
  );

  const pane = (key: InstallSection, body: ReactNode) =>
    allowed(key) ? (
      // Every section stays mounted and is only hidden, so an edit survives a
      // look at another section — the rail marks which ones are holding one.
      <div key={key} className="an-manage-section" data-pane={key} hidden={section !== key}>
        {body}
      </div>
    ) : null;

  return (
    <div className="an-manage">
      {onClose ? (
        <button type="button" className="btn btn-ghost an-manage-back" onClick={() => guard(onClose)}>
          {closeLabel ?? (
            <>
              <ArrowLeft size={14} /> Back to the dashboard
            </>
          )}
        </button>
      ) : null}

      <div className="an-manage-head">
        <div className="an-head-id">
          <span className="an-title-mark" aria-hidden="true">
            {site.platform === 'app' ? <Smartphone size={16} /> : <Globe size={16} />}
          </span>
          <div className="an-head-name">
            <InstallName api={api} site={site} mayManage={mayManage} onChanged={onChanged} onDirty={setRenaming}>
              <span className={`an-health ${chip.tone}`}>
                <i aria-hidden="true" />
                {chip.label}
              </span>
            </InstallName>
            <p className="an-sub">
              {site.platform === 'app' ? 'App install' : 'Website install'} · added {fmtDate(site.createdAt)}
            </p>
          </div>
        </div>
        <div className="an-controls">
          {sites.length > 1 ? (
            <select
              className="select"
              aria-label="Install"
              value={site.id}
              onChange={(event) => {
                const next = Number(event.target.value);
                guard(() => onSelect(next));
              }}
            >
              {sites.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          ) : null}
          {mayManage ? (
            <button type="button" className="btn btn-secondary" onClick={() => guard(() => setAdding(true))}>
              <Plus size={14} /> Add install
            </button>
          ) : null}
        </div>
      </div>

      {!mayManage ? (
        <Note tone="info">
          You can read every report on this install, but changing how it collects is a <b>Manage</b> permission — an
          administrator grants it under Settings → Users &amp; access.
        </Note>
      ) : null}

      <div className="an-manage-split">
        <nav className="an-manage-nav" aria-label="Install settings">
          <span className="an-manage-navgroup">This install</span>
          {visible(INSTALL_SECTIONS).map(navItem)}
          <span className="an-manage-navgroup">Every install</span>
          {visible(SHARED_SECTIONS).map(navItem)}
        </nav>

        <div className="an-manage-pane" ref={paneRef}>
          {pane(
            'install',
            <SetupSection
              api={api}
              site={site}
              sites={sites}
              mayManage={mayManage}
              widgetLinked={widgetLinked}
              geoReady={geo.data ? geo.data.database === 'ready' : null}
              refreshing={refreshing}
              onCheck={onChanged}
              onChanged={onChanged}
              onGo={go}
            />,
          )}
          {pane('collection', <CollectionSection api={api} site={site} onChanged={onChanged} onDirty={onDirty} />)}
          {pane(
            'funnel',
            <FunnelSection
              api={api}
              site={site}
              widgetLinked={widgetLinked}
              onChanged={onChanged}
              onDirty={onDirty}
            />,
          )}
          {pane(
            'privacy',
            <PrivacySection api={api} site={site} onChanged={onChanged} onDirty={onDirty} onGo={go} />,
          )}
          {pane(
            'data',
            <DataSection
              api={api}
              site={site}
              canDelete={sites.length > 1}
              onRemoved={() => {
                // Nothing is left to manage here, and the edits on its other
                // sections went with it — so no guard, straight back out.
                onSelect(0);
                onChanged();
                onRemoved?.();
              }}
              onGo={go}
            />,
          )}
          {pane('location', <LocationSection api={api} status={geo} />)}
        </div>
      </div>

      {adding ? (
        <AddInstallDialog
          api={api}
          onCancel={() => setAdding(false)}
          onCreated={(id) => {
            setAdding(false);
            onCreated(id);
          }}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          title="Discard unsaved changes?"
          body={
            <p>
              {unsaved.join(' and ')} {unsaved.length === 1 ? 'has changes' : 'have changes'} that are not saved.
              Leaving now throws them away.
            </p>
          }
          confirmLabel="Discard changes"
          danger
          onConfirm={() => {
            const action = pending;
            setPending(null);
            action();
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The install's name, renamed where it is read.
 *
 * It used to be the first field of Collection — "what counts, and what is left
 * out", which is not where anybody looks for a name — and that form's copy of
 * it went stale the moment the name changed anywhere else, so the next save of
 * an excluded address would have put the old one back. One place, the title,
 * and a write that names nothing but the name.
 */
function InstallName({
  api,
  site,
  mayManage,
  onChanged,
  onDirty,
  children,
}: {
  api: AnalyticsApi;
  site: AnalyticsSite;
  mayManage: boolean;
  onChanged: () => void;
  /** Whether a rename is open with something in it that is not saved. */
  onDirty: (dirty: boolean) => void;
  /** Beside the name — the health chip — and out of the way while it is edited. */
  children: ReactNode;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // What the write answered, held until the list re-read catches up with it —
  // the app switch's reason: otherwise the title flicks back to the old name for
  // the beat between the two.
  const [held, setHeld] = useState<string | null>(null);
  if (held !== null && held === site.name) setHeld(null);
  const name = held ?? site.name;

  const editing = draft !== null;
  const changed = editing && draft.trim() !== name;
  useEffect(() => onDirty(changed), [onDirty, changed]);

  const close = () => {
    setDraft(null);
    setError(null);
  };

  const save = async () => {
    if (draft === null || !draft.trim()) return;
    if (!changed) return close();
    setBusy(true);
    setError(null);
    try {
      const answer = await api.updateAnalyticsSite(site.id, { name: draft.trim() });
      setHeld(answer.site.name);
      close();
      onChanged();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Could not rename it.');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="an-title-row">
        <h1 className="an-title">{name}</h1>
        {mayManage ? (
          <button
            type="button"
            className="btn btn-ghost btn-icon an-rename-open"
            aria-label="Rename this install"
            title="Rename"
            onClick={() => setDraft(name)}
          >
            <Pencil size={14} />
          </button>
        ) : null}
        {children}
      </div>
    );
  }

  const blank = !draft.trim();
  return (
    <form
      className="an-rename"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <div className="an-title-row">
        <input
          className="input an-rename-input"
          aria-label="Install name"
          value={draft}
          maxLength={INSTALL_NAME_MAX}
          autoFocus
          // Selected whole, the way a rename is: typing replaces the name
          // rather than appending to it.
          onFocus={(event) => event.currentTarget.select()}
          aria-invalid={blank || error ? true : undefined}
          disabled={busy}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') close();
          }}
        />
        <span className="an-rename-actions">
          <button type="submit" className="btn btn-primary" disabled={busy || blank}>
            {busy ? <Spinner /> : null}
            Save
          </button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={close}>
            Cancel
          </button>
        </span>
      </div>
      {blank ? <span className="field-error">An install needs a name.</span> : null}
      {error ? <ErrorNote error={error} /> : null}
    </form>
  );
}

/**
 * The two questions creating an install asks — what it is called and what it
 * is — and nothing else: everything that shapes what it collects is on the
 * screen this lands on, and asking for it up front turns "start measuring my
 * website" into a form.
 */
function InstallForm({
  api,
  onCreated,
  onCancel,
}: {
  api: AnalyticsApi;
  onCreated: (id: number) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<'web' | 'app'>('web');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await api.createAnalyticsSite({ name: name.trim(), platform });
      setName('');
      onCreated(answer.site.id);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Could not create the install.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="an-newinstall-form"
      onSubmit={(event) => {
        event.preventDefault();
        void create();
      }}
    >
      <fieldset className="an-newinstall-kind">
        <legend className="an-set-label">What are you measuring?</legend>
        <div className="an-choices">
          <Choice name="an-new-platform" value="web" current={platform} onChange={setPlatform} title="A website">
            A short tag, pasted into your pages.
          </Choice>
          <Choice name="an-new-platform" value="app" current={platform} onChange={setPlatform} title="A mobile app">
            Your passenger app. Nothing to add to it — creating this switches it on.
          </Choice>
        </div>
      </fieldset>
      <Field
        label="Name"
        hint="Your own label for it — the domain is the obvious choice, and you can rename it later. One install per site or app."
      >
        <input
          className="input"
          value={name}
          placeholder={platform === 'app' ? 'Passenger app' : 'example.com'}
          maxLength={INSTALL_NAME_MAX}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      {error ? <ErrorNote error={error} /> : null}
      <div className="an-newinstall-actions">
        {onCancel ? (
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
          {busy ? <Spinner /> : null}
          {platform === 'app' ? 'Create and switch on' : 'Create and get the code'}
        </button>
      </div>
    </form>
  );
}

function AddInstallDialog({
  api,
  onCreated,
  onCancel,
}: {
  api: AnalyticsApi;
  onCreated: (id: number) => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onCancel()}>
      <div className="dialog an-newinstall-dialog" role="dialog" aria-modal="true" aria-label="Add an install">
        <h2>Add an install</h2>
        <p className="an-newinstall-lede">
          A company with a reservation site and a passenger app gets one of each, and the dashboard can compare them.
        </p>
        <InstallForm api={api} onCreated={onCreated} onCancel={onCancel} />
      </div>
    </div>
  );
}

/**
 * The first install, before there is a dashboard to show: what happens, in the
 * order it happens, beside the form that starts it.
 */
export function NewInstall({ api, onCreated }: { api: AnalyticsApi; onCreated: (id: number) => void }) {
  return (
    <section className="an-onboard">
      <div className="an-onboard-intro">
        <div className="an-onboard-eyebrow">
          <span className="an-title-mark" aria-hidden="true">
            <BarChart3 size={16} />
          </span>
          Website analytics
        </div>
        <h1>Start measuring a website or app</h1>
        <p>Three steps, and the first visits usually show within a minute of the last one.</p>
        <ol className="an-onboard-steps">
          <li>
            <b>Create the install</b>
            <span>Name it, and say whether it is a website or the app.</span>
          </li>
          <li>
            <b>Paste the tag</b>
            <span>A few lines in your pages’ head. The app needs nothing — it finds the install itself.</span>
          </li>
          <li>
            <b>Check it arrives</b>
            <span>The Install screen turns to Reporting once the first visit lands.</span>
          </li>
        </ol>
      </div>
      <div className="an-onboard-form">
        <InstallForm api={api} onCreated={onCreated} />
      </div>
    </section>
  );
}
